import { BaseModel } from './index';

export enum CRMTypeId {
  Konnektive = 1,
  Test = 2,
  Custom = 3,
  Sticky = 4,
  StickyLegacy = 5,
  Sublytics = 6,
  ShopifyPrivate = 7,
  ShopifyPublic = 8,
  Clickbank = 9,
  WooCommerce = 10,
  Response = 11,
  TwentyNineNext = 12,
  CustomExternalRefund = 13,
  Phoenix = 14,
}

export enum CRMTypeName {
  Konnektive = 'Konnektive',
  Test = 'Test',
  Custom = 'Custom',
  Sticky = 'StickyIO',
  StickyLegacy = 'Sticky Legacy',
  Sublytics = 'Vrio',
  ShopifyPrivate = 'Shopify Private Plugin',
  ShopifyPublic = 'Shopify Public Plugin',
  Clickbank = 'Clickbank',
  WooCommerce = 'WooCommerce',
  Response = 'Admoji',
  TwentyNineNext = 'Next Commerce',
  CustomExternalRefund = 'Custom External Refund',
  Phoenix = 'Phoenix',
}

export const CRMTypeNamesMap = {
  [CRMTypeId.Konnektive]: CRMTypeName.Konnektive,
  [CRMTypeId.Test]: CRMTypeName.Test,
  [CRMTypeId.Custom]: CRMTypeName.Custom,
  [CRMTypeId.Sticky]: CRMTypeName.Sticky,
  [CRMTypeId.StickyLegacy]: CRMTypeName.StickyLegacy,
  [CRMTypeId.Sublytics]: CRMTypeName.Sublytics,
  [CRMTypeId.ShopifyPrivate]: CRMTypeName.ShopifyPrivate,
  [CRMTypeId.ShopifyPublic]: CRMTypeName.ShopifyPublic,
  [CRMTypeId.Clickbank]: CRMTypeName.Clickbank,
  [CRMTypeId.WooCommerce]: CRMTypeName.WooCommerce,
  [CRMTypeId.Response]: CRMTypeName.Response,
  [CRMTypeId.TwentyNineNext]: CRMTypeName.TwentyNineNext,
  [CRMTypeId.CustomExternalRefund]: CRMTypeName.CustomExternalRefund,
  [CRMTypeId.Phoenix]: CRMTypeName.Phoenix,
}

// Input types the backend sends for config fields such as processor_config
export enum ConfigFieldType {
  Text = 'text',
  Number = 'number',
  Password = 'password',
  Checkbox = 'checkbox',
  Textarea = 'textarea',
  Timezone = 'timezone',
  Select = 'select',
}

export interface CRMField {
  name: string;
  label?: string;
  type?: string;
  required?: boolean;
  admin_only?: boolean;
  default?: string;
  min?: number;
  max?: number;
  showPassword?: boolean;
  rows?: number;
  help?: string;
  read_only?: boolean;
}

export interface CRMFieldSet extends CRMField {
  fieldset: string;
  fields: CRMField[];
}

export interface CRMTrackingField {
  name: string;
  label?: string;
  read_only?: boolean;
}

export interface PluginType extends BaseModel {
  name: string;
  fields: (CRMField | CRMFieldSet)[];
  encrypted_fields: (CRMField | CRMFieldSet)[];
}

export interface CRMType extends BaseModel {
  name: string;
  fields: (CRMField | CRMFieldSet)[];
  encrypted_fields: (CRMField | CRMFieldSet)[];
  tracking_fields: { [key: string]: CRMTrackingField };
  subscription_plugins: number[];
  auto_config: boolean;
}

export interface CRMTypes {
  crms: CRMType[];
  subscription_plugins: PluginType[];
}

export enum LinkFieldType {
  UniqueId = 1,
  ParentOrderId = 2,
  PreviousOrderId = 3
}

export interface ShopifyPublicPlugin extends BaseModel {
  domain: string;
  enabled: boolean;
  page_id: string;
  require_login: boolean;
  link: string | number;
}

export enum CRMStatus {
  Enabled = 1,
  Disabled = 2,
  Deleted = 3,
}

export interface CRM extends BaseModel {
  name: string;
  display_name: string;
  type: number;
  subscription_plugin: number;
  credentials: { [key: string]: string };
  config: { [key: string]: string };
  tracking_data: { [key: string]: string | null };
  max_fulfillments: number;
  max_transactions: number;
  auto_approve: boolean;
  use_partial_refunds: boolean;
  use_partial_charges: boolean;
  can_update_fulfillment_item: boolean;
  can_cancel_fulfillment_item: boolean;
  can_partial_refund_on_cancel: boolean;
  allow_update_fulfillment: boolean;
  public_key: string;
  private_key: string;
  link_id_field: string;
  link_field_type: LinkFieldType;
  transaction_sync_status: TransactionSyncStatus;
  dispute_management: boolean;
  historic_data_sync_range: string;
  alert_reasons: { [key: string]: string[] };
  status: CRMStatus;
  auto_sync: boolean;
  parent_crms: (string | number)[];
  store_credit_crm: string | number;
  sync_using_batch: boolean;
  shopify_public_plugin: ShopifyPublicPlugin;
  owns_fulfillment: boolean;
}

export interface ShippingType extends BaseModel {
  crm: string | number;
  type_id: string;
  name: string;
  code: string;
  carrier: string;
}

export enum SubscriptionLimit {
  None = 1,
  OnePerOrder = 2,
  OnePerCustomer = 3,
}

export interface CRMCampaign extends BaseModel {
  name: string;
  crm: string | number;
  crm_campaign_id: string;
  campaign: string | number;
  keep_subscriptions_in_sync: boolean;
  separate_next_cycle_items: boolean;
  shipping_methods?: Array<CRMCampaignShippingMethod>;
  subscription_limit: SubscriptionLimit;
  currency: string;
}

export interface CRMCampaignShippingMethod extends BaseModel {
  name: string;
  shipping_id?: string | number;
}

export interface Customer extends BaseModel {
  crm: string | number;
  customer_id: string;
  first_name: string;
  last_name: string;
  company: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  ship_first_name: string;
  ship_last_name: string;
  ship_company: string;
  ship_address1: string;
  ship_address2: string;
  ship_city: string;
  ship_state: string;
  ship_zip: string;
  ship_country: string;
  email: string;
  phone: string;
  default_payment_source: string | number;
  created: string
}

export interface Merchant extends BaseModel {
  merchant_id: string;
  name: string;
  gateway: string;
  mid_number: string;
  descriptor: string;
  mcc: string;
  processor: string;
  enabled: boolean;
  is_solvpath_gateway: boolean;
  solvpath_gateway: string | number;
  crm: string | number;
  crm_name?: string;
}

export enum TransactionSyncStatus {
  DISABLED = 0,
  SYNCING = 1,
  ENABLED = 2
}

export function getCRMCampaignLabel(campaign: CRMCampaign): string {
  return `${campaign.name} (${campaign.crm_campaign_id})`;
}

export function getCRMCustomerUrl(customerId: string, crmType: CRMTypeId | CRMTypeName) {
  let url = null;

  switch (crmType) {
    case CRMTypeId.Konnektive:
    case CRMTypeName.Konnektive:
      url = 'https://crm.konnektive.com/customer/cs/details/?customerId=' + customerId;
      break;
  }

  return url;
}

export function getCRMOrderUrl(orderId: string, crmType: CRMTypeId | CRMTypeName) {
  let url = null;

  switch (crmType) {
    case CRMTypeId.Konnektive:
    case CRMTypeName.Konnektive:
      url = 'https://crm.konnektive.com/customer/cs/orders/?orderId=' + orderId;
      break;
  }

  return url;
}

export function getCRMOrderHtml(orderId: string, actualOrderId: string, crmType: CRMTypeId | CRMTypeName) {
  if (!orderId) {
    return '';
  }
  const url = getCRMOrderUrl(actualOrderId, crmType);

  if (url) {
    return '<a class="blue-link" target="_blank" href="' + url + '">' + orderId + '</a>';
  }

  return orderId;
}

export function getCRMCustomerHtml(customerId: string, crmType: CRMTypeId | CRMTypeName) {
  if (!customerId) {
    return '';
  }
  const url = getCRMCustomerUrl(customerId, crmType);
  if (url) {
    return '<a class="blue-link" target="_blank" href="' + url + '">' + customerId + '</a>';
  }

  return customerId;
}
